import React, { useId } from "react";

/**
 * One product identity for every customer-facing surface.
 *
 * Keep branding centralized so a future trademark, white-label, or reseller
 * decision never requires scattered text and asset changes across modules.
 */
export const PRODUCT_BRAND = Object.freeze({
  // The product label is intentionally independent from a customer account,
  // deployment host, or provider brand. This keeps the shell consistent for
  // every tenant while the workspace name is shown separately.
  name: "RingOi",
  product: "Global",
  fullName: "RingOi Global",
  category: "Global Customer Operations",
  legalStatus: "customer-selected-working-mark",
  markAsset: "/ringoi-mark.svg",
  description:
    "Connection-aware customer operations, service workflows, and analytics.",
  tagline: "Every customer journey, clearly connected.",
});

const joinClasses = (...values) => values.filter(Boolean).join(" ");

/**
 * Compact vector mark: a continuous service ring surrounds an intentionally
 * offset signal point. It represents customer conversations moving through a
 * shared operating loop. `currentColor` keeps it crisp in light, dark, print,
 * high-contrast, and compact navigation treatments.
 */
export function ProductMark({ size = 24, title = "", className = "" }) {
  const generatedId = useId().replace(/:/g, "");
  const titleId = title ? `ringoi-mark-${generatedId}` : undefined;

  return (
    <svg
      className={joinClasses("crm-product-mark", className)}
      width={size}
      height={size}
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role={title ? "img" : undefined}
      aria-hidden={title ? undefined : "true"}
      aria-labelledby={titleId}
      focusable="false"
    >
      {title && <title id={titleId}>{title}</title>}
      <path
        d="M20 7.5a12.5 12.5 0 1 0 0 25 12.5 12.5 0 0 0 10.45-5.63"
        stroke="currentColor"
        strokeWidth="3.75"
        strokeLinecap="round"
      />
      <path
        d="M20 15.9v8.6"
        stroke="currentColor"
        strokeWidth="3.75"
        strokeLinecap="round"
      />
      <circle cx="20" cy="11.4" r="2.15" fill="currentColor" />
      <path
        d="M28.65 13.1c1.28 1.35 2.06 3.17 2.06 5.18"
        stroke="currentColor"
        strokeWidth="2.35"
        strokeLinecap="round"
      />
      <circle cx="30.7" cy="24.8" r="2.55" fill="currentColor" />
    </svg>
  );
}
